import { ROUTES } from './constants';
import { isDealerWorkspacePath } from '../pages/Admin/dealerAdminNav';

export const DEALER_PRODUCTION_HOSTNAME = 'dealer.signalbridges.com';
export const DEALER_RETURN_STORAGE_KEY = 'signalbridge.dealerReturnPath';

function readViteEnv(env) {
  if (env) return env;
  if (typeof import.meta === 'undefined' || !import.meta.env) return {};
  return {
    DEV: import.meta.env.DEV,
    VITE_DEALER_DEV_HOST: import.meta.env.VITE_DEALER_DEV_HOST,
  };
}

function normalizeHostname(hostname) {
  return String(hostname || '')
    .split(':')[0]
    .trim()
    .toLowerCase();
}

/**
 * Presentation hostname only. Never grants Dealer authorization.
 * Production matches dealer.signalbridges.com exactly.
 * Localhost is Dealer-host only when Vite DEV is true and VITE_DEALER_DEV_HOST=1.
 */
export function isDealerHostname(hostname, env) {
  const host = normalizeHostname(hostname);
  if (host === DEALER_PRODUCTION_HOSTNAME) return true;
  const vite = readViteEnv(env);
  const devEnabled = vite.DEV === true && String(vite.VITE_DEALER_DEV_HOST || '') === '1';
  if (!devEnabled) return false;
  return host === 'localhost' || host === '127.0.0.1';
}

export function isSafeDealerReturnPath(pathname) {
  if (typeof pathname !== 'string') return false;
  if (!pathname.startsWith('/') || pathname.startsWith('//')) return false;
  if (pathname.includes('://') || pathname.includes('\\')) return false;
  const path = pathname.split('?')[0].split('#')[0];
  return isDealerWorkspacePath(path);
}

export function rememberDealerReturnPath(pathname, storage) {
  const store = storage || (typeof sessionStorage !== 'undefined' ? sessionStorage : null);
  if (!store || !isSafeDealerReturnPath(pathname)) return false;
  const path = String(pathname).split('?')[0].split('#')[0];
  store.setItem(DEALER_RETURN_STORAGE_KEY, path);
  return true;
}

export function consumeDealerReturnPath(storage) {
  const store = storage || (typeof sessionStorage !== 'undefined' ? sessionStorage : null);
  if (!store) return null;
  const value = store.getItem(DEALER_RETURN_STORAGE_KEY);
  store.removeItem(DEALER_RETURN_STORAGE_KEY);
  return isSafeDealerReturnPath(value) ? value : null;
}

/** Only `/` (and `/login` after auth) are rewritten for the Dealer hostname. `/home` stays. */
export function isDealerHostEntryPath(pathname = '') {
  const path = String(pathname || '').split('?')[0];
  return path === '/' || path === '' || path === ROUTES.LOGIN;
}

export function resolveRootRedirect({ hostname, currentUser, agreementComplete, env } = {}) {
  if (isDealerHostname(hostname, env)) {
    if (currentUser && agreementComplete === false) return ROUTES.ONBOARD;
    return ROUTES.DEALER_WORKSPACE;
  }
  if (!currentUser) return ROUTES.LOGIN;
  if (agreementComplete === false) return ROUTES.ONBOARD;
  return ROUTES.HOME_LAUNCHER;
}

export function resolvePostAuthPath({
  hostname,
  agreementComplete,
  returnPath,
  env,
} = {}) {
  if (agreementComplete === false) return ROUTES.ONBOARD;
  if (isDealerHostname(hostname, env)) {
    if (isSafeDealerReturnPath(returnPath)) return returnPath;
    return ROUTES.DEALER_WORKSPACE;
  }
  return ROUTES.HOME_LAUNCHER;
}

export function rememberPathForDealerLogin({ hostname, pathname, env } = {}, storage) {
  if (!isDealerHostname(hostname, env)) return false;
  if (isSafeDealerReturnPath(pathname)) return rememberDealerReturnPath(pathname, storage);
  return rememberDealerReturnPath(ROUTES.DEALER_WORKSPACE, storage);
}
